import { FC, useEffect, useState, useMemo } from "react";
import DesktopCartView from "./subComponents/DesktopCartView";
import CartMobileView from "./subComponents/CartMobileView";
import { ICartItemHeadless, TCartItem } from "../../types";
import { useTrackContext } from "../../hooks/useTrackContext";
import { ShoppingCartIcon } from "@heroicons/react/solid";
import loadingImageGif from "./../../assets/images/loadingScreenAnimation.gif";
import { GET_CART_LINES } from "../../graphqlQueries";
import { SHOPIFY_STOREFRONT_GRAPHQL_API, SHOPIFY_STOREFRONT_TOKEN } from "../../config";
import RemoveItemConfirmationPopup from "./RemoveItemNotification/RemoveItemConfirmationPopup";
import { LoadingAnimationURL, BattleKartLogoURL } from "../../consts/assetUrls";
import { useLocation } from "react-router-dom";

const MainCart: FC = () => {
  const { productRenderedLocation, headlessCartInfo, headlessCart, headlessCartTotalAmount, isCartEmpty, dispatch} = useTrackContext();
  const [cartdata, setCartData] = useState<TCartItem[]>([]);
  const [headlessCartItems, setheadlessCartItems] = useState<ICartItemHeadless[]>([]);
  const [totalAmount, setTotalAmount] = useState<Number>(0);
  const [totalSaving, setTotalSaving] = useState<Number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const location = useLocation();

  const isBattleKart = location.pathname.includes("battle-kart");

  const isCartEmptyTrue = useMemo(() => {
    return productRenderedLocation === "external" ? headlessCartItems.length === 0 : isCartEmpty;
  }, [productRenderedLocation, headlessCartItems, isCartEmpty]);

  useEffect(() => {
    setheadlessCartItems(headlessCart);
    setTotalAmount(Number(headlessCartTotalAmount));
  }, [headlessCart, headlessCartTotalAmount]);

  useEffect(() => {
    setLoading(true);
    if (productRenderedLocation === "external") {
      const graphqlEndpoint = SHOPIFY_STOREFRONT_GRAPHQL_API;
      const authToken = SHOPIFY_STOREFRONT_TOKEN;

      const headers = {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": authToken,
      };

      const requestOptions = {
        method: "POST",
        headers,
        body: JSON.stringify({
          query: GET_CART_LINES,
          variables: {
            id: headlessCartInfo.id,
          },
        }),
      };

      fetch(graphqlEndpoint, requestOptions)
        .then(async (response) => {
          const responseData = await response.json();
          if (response.status === 200) {
            const cartData = responseData?.data?.cart;
            const subtotal = responseData?.data?.cart?.cost?.subtotalAmount?.amount;
            if (cartData) {
              const cartItems = cartData?.lines?.edges?.map((edge: { node: any }) => edge.node);
              setheadlessCartItems(cartItems);
              setTotalAmount(subtotal ? Number(subtotal) : 0);
              setTotalSaving(0);
            } else {
              console.error("Cart data is missing in the GraphQL response.", responseData);
            }
          } else {
            console.error("GraphQL error:", responseData?.errors);
          }
        })
        .catch((error) => {
          console.error("GraphQL request failed:", error);
        })
        .finally(() => setLoading(false));
    } else {
      window.scrollTo(0, 0);
      fetch("/cart.json")
        .then((response) => {
          if (!response.ok) {
            throw new Error("Network response was not ok");
          }
          return response.json();
        })
        .then((data) => {
          setCartData(data?.items || []);
          setTotalAmount(data?.total_price);
          setTotalSaving(data?.total_discount);
          if (data?.items?.length === 0) {
            dispatch({
              type: "SET_IS_CART_EMPTY",
              payload: {
                isCartEmpty: true,
              },
            });
          } else {
            dispatch({
              type: "SET_IS_CART_EMPTY",
              payload: {
                isCartEmpty: false,
              },
            });
          }
        })
        .catch((error) => {
          console.error("There was a problem with the fetch operation:", error);
        })
        .finally(() => {
          setLoading(false);
        });

      // listen for this custom event
      window.addEventListener("cart:updated", (event: CustomEventInit) => {
        const data = event.detail;
        if (data?.items?.length === 0) {
          dispatch({
            type: "SET_IS_CART_EMPTY",
            payload: {
              isCartEmpty: true,
            },
          });
        }
      });
    }
  }, [dispatch, headlessCartInfo, productRenderedLocation]);

  useEffect(() => {
    if (productRenderedLocation === "internal") {
      const handleCartUpdated = (event: CustomEvent) => {
        const {
          detail: { cart },
        } = event;

        if (cart && !cart.items.length) {
          dispatch({
            type: "SET_IS_CART_EMPTY",
            payload: {
              isCartEmpty: true,
            },
          });
        }
      };

      window.addEventListener("cart:updated", handleCartUpdated as EventListener);

      // Cleanup: remove the event listener when the component unmounts
      return () => {
        window.removeEventListener("cart:updated", handleCartUpdated as EventListener);
      };
    }
  }, [dispatch, productRenderedLocation]);

  return (
    <>
      <div className="container">
        {loading ? (
          <>
            <div className="tw-hidden lg:tw-block">
              <div className="container tw-flex tw-justify-center tw-items-center tw-mt-[100px] tw-mb-[100px] tw-mx-auto tw-w-full">
                {isBattleKart ? (
                  <img src={BattleKartLogoURL} alt="Battle Kart Loading" className="tw-w-[230px] md:tw-w-[300px] tw-h-full" />
                ) : (
                  <img src={"https://hyperkarting-product-component.vercel.app" + loadingImageGif} alt="" className="tw-w-[230px] md:tw-w-[300px] tw-h-full" />
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            {isCartEmptyTrue ? (
              <>
                <div className={`${productRenderedLocation === "external" ? "tw-container" : "container"} tw-mx-auto`}>
                  <div className="tw-flex tw-justify-center tw-items-center">
                    <div className="tw-py-[80px]">
                      <div className="tw-text-[24px] md:tw-text-[30px] tw-text-white tw-text-center tw-mb-[22px] tw-font-[600]">There are no bookings in your cart.</div>
                      <a
                        className={`tw-flex tw-justify-center tw-items-center tw-mx-auto tw-w-fit tw-text-white tw-rounded-[100px] tw-mt-[10px] tw-font-[600] tw-py-[16px] tw-px-[40px] ${
                          isBattleKart 
                            ? "tw-bg-[linear-gradient(180deg,#FAC618_0%,#EA581A_100%)]" 
                            : "tw-bg-[linear-gradient(105.58deg,#9D62FE_12.43%,#A852FF_33.89%,#AD44FF_52.18%,#7886FF_88.75%)]"
                        }`}
                        href="/pages/main-go-karting-track"
                      >
                        Continue Shopping
                      </a>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="tw-hidden lg:tw-block">
                  {productRenderedLocation === "external" && <RemoveItemConfirmationPopup />}
                  <table className={`${productRenderedLocation === "external" ? "tw-container" : "container"} tw-mx-auto tw-mt-[60px] lg:tw-mt-[125px]`}>
                    <thead className="tw-border-b-[2px] tw-border-b-[#555758] tw-w-full">
                      <tr>
                        <th className="tw-text-[#C2C0C3] tw-text-[16px] tw-text-left">Product</th>
                        <th className="tw-text-[#C2C0C3] tw-text-[16px] tw-text-left">Price</th>
                        <th className="tw-text-[#C2C0C3] tw-text-[16px] tw-text-left">Quantity</th>
                        <th className="tw-text-[#C2C0C3] tw-text-[16px] tw-text-left">Total</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody className="tw-w-full">
                      {productRenderedLocation === "external" && headlessCartItems.length !== 0 ? (
                        <>
                          {(() => {
                            // Group items by type
                            const battleKartItems = headlessCartItems.filter((item: ICartItemHeadless) => 
                              item.merchandise.product.title.toLowerCase().includes("battle") ||
                              item.merchandise.title.toLowerCase().includes("battle")
                            );
                            const racingItems = headlessCartItems.filter((item: ICartItemHeadless) => 
                              !item.merchandise.product.title.toLowerCase().includes("battle") &&
                              !item.merchandise.title.toLowerCase().includes("battle")
                            );

                            return (
                              <>
                                {racingItems.length > 0 && (
                                  <>
                                    <tr>
                                      <td colSpan={5} className="tw-py-[20px] tw-px-0">
                                        <div className="tw-text-white tw-text-[26px] tw-font-[700] tw-pb-[2px] tw-border-b-[1px] tw-border-b-[#555758]">
                                          Racing
                                        </div>
                                      </td>
                                    </tr>
                                    {racingItems.map((item: ICartItemHeadless) => (
                                      <DesktopCartView
                                        headlessVariantID={item.id}
                                        productTittle={item.merchandise.product.title}
                                        key={item.merchandise.id}
                                        image={item.merchandise.image.url}
                                        variantTitle={item.merchandise.title}
                                        title={item.merchandise.product.title}
                                        price={Number(item.merchandise.priceV2.amount)}
                                        compareAtPrice={item.merchandise.compareAtPrice ? Number(item.merchandise.compareAtPrice.amount) : 0}
                                        quantity={item.quantity}
                                        id={item.merchandise.id}
                                        gameOptions={item.merchandise.selectedOptions}
                                      />
                                    ))}
                                  </>
                                )}
                                {battleKartItems.length > 0 && (
                                  <>
                                    <tr>
                                      <td colSpan={5} className="tw-py-[20px] tw-px-0">
                                        <div className="tw-text-white tw-text-[26px] tw-font-[700] tw-pb-[2px] tw-border-b-[1px] tw-border-b-[#555758] add-grobold-font">
                                          BattleKart
                                        </div>
                                      </td>
                                    </tr>
                                    {battleKartItems.map((item: ICartItemHeadless) => (
                                      <DesktopCartView
                                        headlessVariantID={item.id}
                                        productTittle={item.merchandise.product.title}
                                        key={item.merchandise.id}
                                        image={item.merchandise.image.url}
                                        variantTitle={item.merchandise.title}
                                        title={item.merchandise.product.title}
                                        price={Number(item.merchandise.priceV2.amount)}
                                        compareAtPrice={item.merchandise.compareAtPrice ? Number(item.merchandise.compareAtPrice.amount) : 0}
                                        quantity={item.quantity}
                                        id={item.merchandise.id}
                                        gameOptions={item.merchandise.selectedOptions}
                                      />
                                    ))}
                                  </>
                                )}
                              </>
                            );
                          })()}
                        </>
                      ) : (
                        <>
                          {cartdata.length !== 0 && (() => {
                            // Group items by type
                            const validItems = cartdata.filter(item => !item.title.toLowerCase().includes("booking protection"));
                            const battleKartItems = validItems.filter(item => 
                              item.title.toLowerCase().includes("battle") ||
                              item.product_title.toLowerCase().includes("battle") ||
                              item.variant_title?.toLowerCase().includes("battle")
                            );
                            const racingItems = validItems.filter(item => 
                              !item.title.toLowerCase().includes("battle") &&
                              !item.product_title.toLowerCase().includes("battle") &&
                              !item.variant_title?.toLowerCase().includes("battle")
                            );

                            return (
                              <>
                                {racingItems.length > 0 && (
                                  <>
                                    <tr>
                                      <td colSpan={5} className="tw-py-[20px] tw-px-0">
                                        <div className="tw-text-white tw-text-[26px] tw-font-[700] tw-pb-[2px] tw-border-b-[1px] tw-border-b-[#555758]">
                                          Racing
                                        </div>
                                      </td>
                                    </tr>
                                    {racingItems.map((item) => (
                                      <DesktopCartView
                                        key={item.key}
                                        image={item.featured_image.url}
                                        productTittle={item.product_title}
                                        variantTitle={item.variant_title}
                                        title={item.title}
                                        price={item.price}
                                        compareAtPrice={Number(item.original_line_price)}
                                        productType={item.product_type}
                                        quantity={item.quantity}
                                        id={item.key}
                                        gameOptions={item.options_with_values}
                                      />
                                    ))}
                                  </>
                                )}
                                {battleKartItems.length > 0 && (
                                  <>
                                    <tr>
                                      <td colSpan={5} className="tw-py-[20px] tw-px-0">
                                        <div className="tw-text-white tw-text-[26px] tw-font-[700] tw-pb-[2px] tw-border-b-[1px] tw-border-b-[#555758] add-grobold-font">
                                          BattleKart
                                        </div>
                                      </td>
                                    </tr>
                                    {battleKartItems.map((item) => (
                                      <DesktopCartView
                                        key={item.key}
                                        image={item.featured_image.url}
                                        productTittle={item.product_title}
                                        variantTitle={item.variant_title}
                                        title={item.title}
                                        price={item.price}
                                        compareAtPrice={Number(item.original_line_price)}
                                        productType={item.product_type}
                                        quantity={item.quantity}
                                        id={item.key}
                                        gameOptions={item.options_with_values}
                                      />
                                    ))}
                                  </>
                                )}
                              </>
                            );
                          })()}
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {loading ? (
        <>
          <div className="tw-block lg:tw-hidden">
            <div className="container tw-flex tw-justify-center tw-items-center tw-mt-[100px] tw-mb-[100px] tw-mx-auto tw-w-full">
              {isBattleKart ? (
                <img src={BattleKartLogoURL} alt="Battle Kart Loading" className="tw-w-[230px] md:tw-w-[300px]" />
              ) : (
                <img src={LoadingAnimationURL} alt="" className="tw-w-[230px] md:tw-w-[300px]" />
              )}
            </div>
          </div>
        </>
      ) : (
        <>
          {!isCartEmptyTrue && (
            <>
              <div className={`${productRenderedLocation === "external" ? "tw-container tw-px-[10px] md:tw-px-[0]" : "container"} tw-mx-auto`}>
                <div className="tw-block lg:tw-hidden">
                  <RemoveItemConfirmationPopup />
                  {productRenderedLocation === "external" ? (
                    <>
                      {headlessCartItems.length !== 0 && (() => {
                        // Group items by type
                        const battleKartItems = headlessCartItems.filter((item: ICartItemHeadless) => 
                          item.merchandise.product.title.toLowerCase().includes("battle") ||
                          item.merchandise.title.toLowerCase().includes("battle")
                        );
                        const racingItems = headlessCartItems.filter((item: ICartItemHeadless) => 
                          !item.merchandise.product.title.toLowerCase().includes("battle") &&
                          !item.merchandise.title.toLowerCase().includes("battle")
                        );

                        return (
                          <>
                            {racingItems.length > 0 && (
                              <>
                                <div className="tw-text-white tw-text-[20px] tw-font-[700] tw-pb-[2px] tw-mb-[20px] tw-border-b-[1px] tw-border-b-[#555758]">
                                  Racing
                                </div>
                                {racingItems.map((item: ICartItemHeadless) => (
                                  <CartMobileView
                                    headlessVariantID={item.id}
                                    key={item.merchandise.id}
                                    productTittle={item.merchandise.product.title}
                                    variantTitle={item.merchandise.title}
                                    image={item.merchandise.image.url}
                                    title={item.merchandise.product.title}
                                    price={Number(item.merchandise.priceV2.amount)}
                                    quantity={item.quantity}
                                    id={item.merchandise.id}
                                    compareAtPrice={item.merchandise.compareAtPrice ? Number(item.merchandise.compareAtPrice.amount) : 0}
                                    gameOptions={item.merchandise.selectedOptions}
                                  />
                                ))}
                              </>
                            )}
                            {battleKartItems.length > 0 && (
                              <>
                                <div className="tw-text-white tw-text-[20px] tw-font-[700] tw-pb-[2px] tw-mb-[20px] tw-border-b-[1px] tw-border-b-[#555758] add-grobold-font">
                                  BattleKart
                                </div>
                                {battleKartItems.map((item: ICartItemHeadless) => (
                                  <CartMobileView
                                    headlessVariantID={item.id}
                                    key={item.merchandise.id}
                                    productTittle={item.merchandise.product.title}
                                    variantTitle={item.merchandise.title}
                                    image={item.merchandise.image.url}
                                    title={item.merchandise.product.title}
                                    price={Number(item.merchandise.priceV2.amount)}
                                    quantity={item.quantity}
                                    id={item.merchandise.id}
                                    compareAtPrice={item.merchandise.compareAtPrice ? Number(item.merchandise.compareAtPrice.amount) : 0}
                                    gameOptions={item.merchandise.selectedOptions}
                                  />
                                ))}
                              </>
                            )}
                          </>
                        );
                      })()}
                    </>
                  ) : (
                    <>
                      {cartdata.length !== 0 && (() => {
                        // Group items by type
                        const validItems = cartdata.filter(item => !item.title.toLowerCase().includes("booking protection"));
                        const battleKartItems = validItems.filter(item => 
                          item.title.toLowerCase().includes("battle") ||
                          item.product_title.toLowerCase().includes("battle") ||
                          item.variant_title?.toLowerCase().includes("battle")
                        );
                        const racingItems = validItems.filter(item => 
                          !item.title.toLowerCase().includes("battle") &&
                          !item.product_title.toLowerCase().includes("battle") &&
                          !item.variant_title?.toLowerCase().includes("battle")
                        );

                        return (
                          <>
                            {racingItems.length > 0 && (
                              <>
                                <div className="tw-text-white tw-text-[20px] tw-font-[700] tw-pb-[2px] tw-mb-[20px] tw-border-b-[1px] tw-border-b-[#5555758]">
                                  Racing
                                </div>
                                {racingItems.map((item) => (
                                  <CartMobileView
                                    key={item.key}
                                    image={item.featured_image.url}
                                    title={item.title}
                                    productTittle={item.product_title}
                                    price={item.price}
                                    productType={item.product_type}
                                    quantity={item.quantity}
                                    id={item.key}
                                    compareAtPrice={item.original_line_price}
                                    gameOptions={item.options_with_values}
                                  />
                                ))}
                              </>
                            )}
                            {battleKartItems.length > 0 && (
                              <>
                                <div className="tw-text-white tw-text-[20px] tw-font-[700] tw-pb-[2px] tw-mb-[20px] tw-border-b-[1px] tw-border-b-[#555758] add-grobold-font">
                                  BattleKart
                                </div>
                                {battleKartItems.map((item) => (
                                  <CartMobileView
                                    key={item.key}
                                    image={item.featured_image.url}
                                    title={item.title}
                                    productTittle={item.product_title}
                                    price={item.price}
                                    productType={item.product_type}
                                    quantity={item.quantity}
                                    id={item.key}
                                    compareAtPrice={item.original_line_price}
                                    gameOptions={item.options_with_values}
                                  />
                                ))}
                              </>
                            )}
                          </>
                        );
                      })()}
                    </>
                  )}
                </div>

                <div className="tw-my-[40px]">
                  <div className="tw-full tw-block">
                    <div className="tw-flex tw-justify-between lg:tw-text-right lg:tw-block">
                      <span className="tw-text-[18px] tw-font-[600] tw-text-white lg:tw-mr-[2em]">Subtotal:</span>
                      <span className="tw-text-[30px] tw-font-[700] tw-text-white mini__cart__footer__txt__price">
                        ${productRenderedLocation === "external" ? Number(totalAmount).toFixed(2) : (Number(totalAmount) / 100).toFixed(2)}
                      </span>
                    </div>
                    <div className="tw-flex tw-justify-between lg:tw-text-right lg:tw-block">
                      <span className="tw-text-[16px] tw-font-[500] tw-text-white lg:tw-mr-[4em]">Total Savings:</span>
                      <span className="tw-text-[16px] tw-font-[500] tw-text-white">${(Number(totalSaving) / 100).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
                <div className="tw-flex tw-justify-center tw-items-center">
                  <div className="md:tw-max-w-[551px] md:tw-w-full">
                    <span className="tw-text-[13px] tw-font-[500] tw-text-white tw-block tw-my-[20px] tw-text-center tw-flex tw-items-center tw-justify-center">
                      Get <span className="tw-font-[700] tw-px-1">$19 off</span> with Hyper Karting
                      <a href="https://hyperkarting.com.au/products/membership">
                        <img
                          src="https://cdn.shopify.com/s/files/1/0496/8733/0976/files/HK-Membership_Icon_a819308e-9913-473d-b1ac-b5aa3d061947.png?v=1695350485"
                          alt="membership logo"
                          style={{
                            maxWidth: "150px",
                            width: "100%",
                          }}
                        />
                      </a>
                    </span>
                    <div className="tw-flex tw-flex-col tw-justify-center md:tw-flex-row tw-gap-[20px]">
                      <button
                        id="Book-Now"
                        onClick={() =>
                          productRenderedLocation === "external"
                            ? (window.location.href = "https://hyperkarting.com.au/pages/main-go-karting-track")
                            : (window.location.href = "/pages/main-go-karting-track")
                        }
                        className={`tw-text-center tw-font-extrabold tw-text-[medium] tw-py-[16px] tw-px-[40px] tw-border tw-rounded-full tw-cursor-pointer ${
                          isBattleKart 
                            ? "tw-text-[#EA581A] tw-border-[#EA581A]" 
                            : "tw-text-[#9D62FE] tw-border-[#9D62FE]"
                        }`}
                      >
                        Continue Shopping
                      </button>
                      <button
                        id="Add-Cart"
                        onClick={() => {
                          productRenderedLocation === "external" ? (window.location.href = headlessCartInfo.checkoutUrl) : (window.location.href = "/checkout")
                        }}
                        className={`tw-flex tw-items-center tw-justify-center tw-text-center tw-py-[16px] tw-px-[40px] tw-rounded-full ${
                          isBattleKart 
                            ? "tw-text-white tw-cursor-pointer tw-bg-[linear-gradient(180deg,#FAC618_0%,#EA581A_100%)]" 
                            : "tw-text-white tw-cursor-pointer bg-gradient-2"
                        }`}
                      >
                        <ShoppingCartIcon className="tw-w-[28px] tw-h-[28px]" />
                        <span className="tw-font-extrabold tw-text-[medium] tw-ml-[10px]">Checkout</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </>
  );
};

export default MainCart;
